/**
 * src/pages/OriginAndBeyond/sections/IntegritySection.tsx
 * Section 5: "Honest about the unknown" integrity section using DisclaimerNote:
 * - Science describes the origin of the universe back to a tiny fraction of a second, but not what (if anything) came before
 * - Consciousness remains unexplained (the hard problem)
 * - Current science does not establish what, if anything, happens to subjective experience after death
 * - Quantum physics is not proof of an afterlife
 * - Beliefs deserve respect as lived cultural traditions
 */

import React from "react";
import { DisclaimerNote } from "@/components/originBeyond/DisclaimerNote";
import "@/components/cosmic/cosmic.css";
export function IntegritySection() {
  return <section className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" aria-labelledby="integrity-heading">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <h2 id="integrity-heading" className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-foreground mb-2">
          Honest About the Unknown
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed">
          The power of the scientific method rests upon clearly articulating where empirical data
          ends and speculative hypotheses or subjective meaning begins.
        </p>
      </div>

      <div className="flex flex-col gap-4 sm:gap-5">
        <DisclaimerNote tone="science" title="Empirical Frontiers & Scientific Boundaries">
          Modern physical cosmology accurately reconstructs cosmic evolution back to a tiny fraction
          of a second after the primordial expansion began (inflation and Big Bang nucleosynthesis).
          However, current physics cannot describe what—if anything—preceded that initial state, as
          general relativity breaks down at the Planck scale. In the study of mind, neuroscience
          maps extensive neural correlates of sensation and cognition, yet how physical brain
          activity generates first-person subjective experience remains fundamentally unsolved.
        </DisclaimerNote>

        <DisclaimerNote tone="science" title="Quantum Physics Is Not Proof of an Afterlife">
          Quantum liaison phenomena such as superposition, entanglement, and tunneling describe
          subatomic behavior under rigorous mathematical constraints. They are not empirical
          proof of an afterlife, disembodied awareness, or conscious intention creating physical
          reality. Near-death experiences (NDEs) provide valuable neurochemical insights into how
          hypoxic brains respond to crisis, but they neither prove nor disprove the post-mortem
          survival of consciousness. Current science does not establish what, if anything, happens
          to subjective experience after biological death.
        </DisclaimerNote>

        <DisclaimerNote tone="respect" title="Cultural Integrity & Humility">
          For millennia, spiritual, indigenous, and philosophical traditions have offered distinct
          narratives concerning creation, purposeful existence, and mortality. Across Quantum
          Liaison, cultural and religious perspectives (such as Christian, Buddhist, Islamic, and
          indigenous cosmologies) are presented objectively in the spirit of intellectual history
          and human dignity—never ranked, never ridiculed, and never conflated with empirical
          astrophysical measurements.
        </DisclaimerNote>
      </div>
    </section>;
}